import axios from 'axios';
import { useCallback, useState } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { ReturnForm, Sale, ShipmentForm, ShipmentFormStatus, Vehicle } from '../../../../system/Domain';
import ask from 'Dialogs/ask';
import useForm from 'system/useForm';
import useAsyncEffect from 'system/useAsyncEffect';
import { Step } from '../../../../Components/FormWizard';
import FormWizard from '../../../../Components/FormWizard';
import implementIData from './implementIData';
import DAP01Vin from './DAP01Vin';
import DAP02ConditionAndEquipment from './DAP02ConditionAndEquipment';
import DAP03KeysAndAccessories from './DAP03KeysAndAccessories';
import DAP04Wheels from './DAP04Wheels';
import DAP05RemarksInterior from './DAP05RemarksInterior';
import DAP06RemarksExterior from './DAP06RemarksExterior';
import DAP07RemarksOther from './DAP07RemarksOther';
import DAP08Pickup from './DAP08Pickup';
import DAP09Signatures from './DAP09Signatures';
import showMessage from 'Dialogs/showMessage';
import { useHistory } from 'react-router-dom';
import { ShippingSalePartial } from "./ShipmentFormPage";

interface Props {
	sale: ShippingSalePartial
	vehicle: Vehicle
	shipmentFormIndex: number
	onFormClosed?: (sf: ShipmentForm) => void
}

const DAPForm = (props: Props) => {
	// always verify the logic of checkActiveStep function (see below) when doing step rearrengement !!!
	const steps: Step[] = [
		{
			title: 'Fahrgestellnummer',
			ui: stepProps => <DAP01Vin {...stepProps} />
		},
		{
			title: 'Fahrzeugzustand & Ausstattung',
			ui: stepProps => <DAP02ConditionAndEquipment {...stepProps} />
		},
		{
			title: 'Schlüssel & Weitere Ausstattung',
			ui: stepProps => <DAP03KeysAndAccessories {...stepProps} />
		},
		{
			title: 'Rädern und Felgen',
			ui: stepProps => <DAP04Wheels {...stepProps} />
		},
		{
			title: 'Reklamation Interieur',
			ui: stepProps => <DAP05RemarksInterior {...stepProps} />
		},
		{
			title: 'Reklamation Exterieur',
			ui: stepProps => <DAP06RemarksExterior  {...stepProps} />
		},
		{
			title: 'Reklamation Anderes',
			ui: stepProps => <DAP07RemarksOther  {...stepProps} />
		},
		{
			title: 'Fahrzeugabholung',
			ui: stepProps => <DAP08Pickup {...stepProps} />
		},
		{
			title: 'Unterschriften',
			ui: stepProps => <DAP09Signatures {...stepProps} vehicle={props.vehicle} />
		}
	];

	const [shipmentFormData, setShipmentFormData] = useState(implementIData(props.sale.shipping?.shipmentForms[props.shipmentFormIndex]));

	const [comparisonData, setComparisonData] = useState<ShipmentForm>(null);
	const [isLoading, setIsLoading] = useState(true);
	const history = useHistory();

	// load last ReturnForm data - to show for comparison
	useAsyncEffect(async () => {
		if (!props.vehicle || shipmentFormData?.isReadonly()) {
			// when form is already closed, we don't want to load and show comparison data
			setIsLoading(false);
			return;
		}

		const { data: returnForms } = await axios.post<ReturnForm[]>('/api/orders/transports/return-forms/search',
			{
				vin: props.vehicle.vin
			}
		);

		// (forms search returns them in descendening order - and we want to take the newest one)
		if (returnForms.length > 0) {
			const comparisonShipmentForm: ShipmentForm = {
				status: ShipmentFormStatus.started,
				dateUpdated: returnForms[0].dateUpdated,
				dateCreated: returnForms[0].dateCreated,
				createdBy: returnForms[0].createdBy,
				updatedBy: returnForms[0].updatedBy,
				hasReturnForm: !!returnForms[0],
				conditionAndEquipment: {
					inspectionReports: returnForms[0].readiness.inspectionReports,
					correctVin: returnForms[0].readiness.correctVin,
					mileage: returnForms[0].transfer.mileage,
					serviceBooklet: returnForms[0].documentsAndEquipment.serviceBooklet,
					serviceRecord: returnForms[0].documentsAndEquipment.serviceRecord,
					radio: returnForms[0].documentsAndEquipment.radio,
					navigationDevice: returnForms[0].documentsAndEquipment.navigationDevice,
					navigationDataMedium: returnForms[0].documentsAndEquipment.navigationDataMedium
				},
				keys: {
					vehicleKeys: returnForms[0].documentsAndEquipment.keys
				},
				accessories: {
					bootCover: returnForms[0].accessories.bootCover,
					loadSecuringNet: returnForms[0].accessories.loadSecuringNet,
					isChargingCable: returnForms[0].accessories.chargingCable
				},
				installedTiresAndRims: returnForms[0].installedTiresAndRims,
				additionalTiresAndRims: returnForms[0].additionalTiresAndRims,
				suppliedTiresAndRims: returnForms[0].suppliedTiresAndRims
			};

			setComparisonData(comparisonShipmentForm);
		}

		setIsLoading(false);
	}, [props.vehicle?.vin]);

	const formSave = useCallback(async (sf: ShipmentForm) => {
		const { data: updatedSale } = await axios.put<Sale>(`/api/sales/${props.sale.id}/shipment-form/${props.shipmentFormIndex}`, sf);

		const updatedShipmentForm = updatedSale.shipping.shipmentForms[props.shipmentFormIndex];

		setShipmentFormData(implementIData(updatedShipmentForm));
	}, [props.sale.id]);

	const formClose = useCallback(async (sf: ShipmentForm) => {
		const { yes } = await ask("Wollen Sie das Ausgangsprotokoll abschließen?");

		if (yes) {
			const { data: updatedSale } = await axios.put<Sale>(`/api/sales/${props.sale.id}/shipment-form/${props.shipmentFormIndex}/close`);

			const updatedSf = updatedSale.shipping.shipmentForms[props.shipmentFormIndex];
			setShipmentFormData(implementIData(updatedSf));

			if (props.onFormClosed) {
				props.onFormClosed(updatedSf);
			}
		}
	}, [props.sale.id]);

	const checkActiveStep = useCallback(async (form, active): Promise<boolean> => {
		const isCorrectVin = !!form.getValue("conditionAndEquipment.correctVin");
		if ((active === 0) && !isCorrectVin) {
			// this is Vin step, cannot continue if Vin is not correct
			await showMessage({ title: "Fehler", message: "Fahrgestellnummer muss KORREKT und ÜBERPRÜFT sein.", color: "error" });
			return false;
		}

		return true;
	}, [props.sale.id]);

	const comparisonForm = useForm({
		values: comparisonData,
		setValues: null
	});

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
			{isLoading && (
				<CircularProgress />
			)}
			<FormWizard
				dataObject={shipmentFormData}
				steps={steps}
				onSave={formSave}
				onComplete={formClose}
				onReturn={() => history.goBack()}
				onNextStep={checkActiveStep}
				dirtyFormMessage="Die Änderungen wurden noch nicht gespeichert, beim Verlassen der Seite gehen die Änderungen verloren."
				stepsProps={!shipmentFormData?.isReadonly() ? comparisonForm : null}
			/>
		</Box>
	);
};

export default DAPForm;
